//done
import * as config from "../constants.js";
import { updateUI, updateHeaderBackground, updateTabVisibility, updateFactionAvailability, checkAndApplyPendingPayments } from "./game.js";
import { minContractHoursToEarn, contractCompletionReward } from "./raidEconomics-core.js";
import { liveCharacter } from "./gameState-core.js";
import { openCraftingModal } from "./crafting.js";
import { gameState, saveGameState } from "./gameState.js";
import { addNotification } from "./notifications.js";
import { updateInventoryCharacterHeader, createInventoryCard } from "./inventory.js";
import { refreshAlpineInventory } from "./inventoryManager.js";
import { registerSubTabs, applySubTabs, setSubTab } from "./subTabs.js";
import { registerSkillTabs, activeSkillTab, skillTabStrip } from "./skillTabs.js";
import { renderGearTab } from "./gear.js";
import { multiplayer, validateCharacterName } from "./multiplayer.js";
import { getSkillDisplayName, findItem, getTechniqueSkill, debounce, getAvatarSeed, avatarDataAttrs } from "./util.js";
import { featuresFromSeed, FEATURE_SCHEMA } from "./avatarGen.js";
import { mountAvatarAnimation, stopMainAvatar } from "./avatarView.js";
import { updateGridTeamDisplay } from "./raid.js";
import { isCommandRevealed, calculateCommandMarks, getUpgradeEffect, claimVaultForNewCharacter } from "./command-core.js";
import { getSkillCheckChance, getSynergyPartnersForTechnique } from "./eventSystem-core.js";
import { debug, isDebug, CH } from './debugLog.js';
import {
  calculateXPToNextLevel,
  gainExperienceCore,
  levelUpCore,
  upgradeSkillCore,
  calculateSkillCurrencyCost,
  skillPointCostFor,
  calculateEnergyRegen,
  updateEnergyOverTime,
  processCharacterHealthRegeneration,
  isSynergyRevealed,
  isSynergyUnlocked,
  getSynergyUnlockConfig,
  unlockSkillSynergyCore,
  getSkillArtifactBonus,
  applyStartingLevel,
  hpGainForLevel,
} from "./character-core.js";
export { calculateEnergyRegen, calculateXPToNextLevel, updateEnergyOverTime, processCharacterHealthRegeneration };

// ---------------------------------------------------------------------------
// Character sub-tabs (Skills / Inventory / Gear)
//
// The character page's right column swaps between three panes. The selection
// is module-scoped so it persists across re-renders AND character switches
// within a session (resets to Skills on reload).
// ---------------------------------------------------------------------------
// Registered with the shared helper rather than hand-rolled here. This file
// used to own the ONLY implementation of sub-tabs; Command, Syndicate and
// gear.js all needed the same thing, and the subtle part (the bounded sizing
// retry) is exactly what gets lost when a pattern is re-typed. See subTabs.js.
registerSubTabs("character", {
  panes: {
    skills: "char-skills-pane",
    inventory: "inventory-tab",
    gear: "gear-tab",
  },
  scrollers: { skills: "skills-grid", inventory: "inventory-grid", gear: "gear-tab" },
  defaultTab: "skills",
  // Panes are hidden, not unmounted, so anything state-dependent has to be
  // refreshed on reveal (gear.js self-exposes renderGearTab; inventory is
  // Alpine-reactive but re-syncs USE/EQUIP/SELL against current HP/energy).
  onShow: (name) => {
    if (name === "gear") renderGearTab();
    else if (name === "inventory") refreshAlpineInventory();
  },
});

// The Skills pane shows one skill at a time. Five cards in a 2-3 column grid
// was three screens of scrolling on a tablet; the strip is one row.
const SKILL_TAB_GROUP = "char-skills";
registerSkillTabs(SKILL_TAB_GROUP, () => displayCharacter());

export function applyCharSubTab() {
  applySubTabs("character");
}

export function setCharSubTab(name) {
  setSubTab("character", name);
}

if (typeof window !== "undefined") {
  window.setCharSubTab = setCharSubTab;
  window.applyCharSubTab = applyCharSubTab;
  window.toggleCharacterCard = toggleCharacterCard;
  // The resize listener lives in subTabs.js now, and re-measures every
  // registered group rather than just this page's.
}

//
// Technique tab switching
//
window.switchTechniqueTab = function (tabGroupId, tabIndex) {
  // Update tab buttons
  const tabs = document.querySelectorAll(`[data-tab-group="${tabGroupId}"][data-tab-index]`);
  tabs.forEach((tab, idx) => {
    if (idx === tabIndex) {
      tab.classList.remove("bg-gray-500/60", "hover:bg-gray-600/60");
      tab.classList.add("bg-black/65");
    } else {
      tab.classList.remove("bg-black/65");
      tab.classList.add("bg-gray-500/60", "hover:bg-gray-600/60");
    }
  });

  // Update panels
  const panels = document.querySelectorAll(`.technique-panel[data-tab-group="${tabGroupId}"]`);
  panels.forEach((panel, idx) => {
    if (idx === tabIndex) {
      panel.classList.remove("hidden");
    } else {
      panel.classList.add("hidden");
    }
  });

  // Remember the selected tab for this skill
  if (!gameState.character.techniqueTabSelections) {
    gameState.character.techniqueTabSelections = {};
  }
  gameState.character.techniqueTabSelections[tabGroupId] = tabIndex;
  saveGameState();
};

//
// Artifact related functions
//

// Helper export function to check if an artifact can be equipped
export function canEquipArtifact(artifact) {
  if (!gameState.character) return false;

  // Check if artifact requires a specific skill and if character has it
  debug(CH.ui, "canEquip", artifact);
  if (artifact.slot === "skillSlot" && artifact.skill) {
    const characterSkillLevel = gameState.character.skills[artifact.skill] || 0;
    return characterSkillLevel > 0;
  }

  return true; // Utility artifacts can always be equipped
}

// Helper export function to get skill requirement error message
export function getArtifactSkillRequirementMessage(artifact) {
  if (!artifact.skill || artifact.slot !== "skillSlot") return "";

  // Get the human-readable skill name
  let skillName = getSkillDisplayName(artifact.skill);
  return `Requires ${skillName} skill`;
}

export function equipArtifact(artifactId) {
  debug(CH.ui, artifactId, gameState.character.artifacts);
  const artifact = gameState.character.artifacts.find((a) => a.id === artifactId);
  if (!artifact) {
    addNotification("Artifact not found!", "error");
    return false;
  }

  // Check if artifact can be equipped
  if (!canEquipArtifact(artifact)) {
    const message = getArtifactSkillRequirementMessage(artifact);
    addNotification(`Cannot equip ${artifact.name} - ${message.toLowerCase()}!`, "error");
    return false;
  }

  const slotName = artifact.slot;
  const currentEquipped = gameState.character.equippedArtifacts[slotName];

  // Unequip current artifact if any
  if (currentEquipped) {
    unequipArtifact(slotName);
  }

  // Equip new artifact
  gameState.character.equippedArtifacts[slotName] = artifact;
  addNotification(`Equipped ${artifact.name}!`, "success");

  // Refresh inventory display to update equipped status
  refreshAlpineInventory();
  window.renderGearTab?.(); // gear-tab cards show equip state

  saveGameState();
  displayCharacter();
  return true;
}

export function unequipArtifact(slotName) {
  // Prevent unequipping artifacts for marketplace characters
  if (gameState.character.isMarketplace) {
    addNotification("Cannot modify equipment - character is on marketplace", "error");
    return false;
  }

  const artifact = gameState.character.equippedArtifacts[slotName];
  if (!artifact) return false;

  gameState.character.equippedArtifacts[slotName] = null;
  addNotification(`Unequipped ${artifact.name}!`, "info");

  // Refresh inventory display to update equipped status
  refreshAlpineInventory();
  window.renderGearTab?.(); // gear-tab cards show equip state

  saveGameState();
  displayCharacter();
  return true;
}

export function canEquipTechniqueGear(gearId, techniqueId = null) {
  debug(CH.ui, "canEquipGear", gearId);

  if (!gameState.character) return false;
  // Check item requirements
  const gearItem = findItem("id", gearId);
  if (gearItem.type !== "techGear") {
    return false;
  }
  // Check if character has related skill
  const characterSkill = gameState.character.skills[getTechniqueSkill(gearItem.techniqueId)];
  if (!characterSkill) return false;

  // Validate gear matches technique ID if provided
  if (techniqueId && gearItem.techniqueId !== techniqueId) {
    return false;
  }

  return true;
}

// Helper export function to get skill requirement error message
export function getTechniqueGearRequirementMessage(gearId) {
  const gearItem = findItem("id", gearId);
  if (!gearItem || !gearItem.techniqueId) return "";

  // Get the human-readable skill name
  let skillName = getSkillDisplayName(getTechniqueSkill(gearItem.techniqueId));
  return `Requires ${skillName} skill`;
}

// Equip technique-specific gear
export function equipTechniqueGear(techniqueId, gearId) {
  const gearItem = findItem("id", gearId);
  // Prevent equipping gear for marketplace characters
  if (gameState.character.isMarketplace) {
    addNotification("Cannot modify equipment - character is on marketplace", "error");
    return false;
  }

  if (!canEquipTechniqueGear(gearId, techniqueId)) {
    addNotification("Gear requirements not met", "error");
    return false;
  }

  // One gear per skill: equipping evicts whatever same-skill technique is
  // tooled up (incl. this technique's own current gear). The full kit is a
  // toolbox - swap freely between raids to match the route.
  const skillKey = getTechniqueSkill(techniqueId);
  Object.keys(gameState.character.equippedTechniqueGear).forEach(equippedTechId => {
    if (getTechniqueSkill(equippedTechId) === skillKey) {
      unequipTechniqueGear(equippedTechId);
    }
  });

  // Equip new gear
  gameState.character.equippedTechniqueGear[techniqueId] = gearItem;
  addNotification(`Equipped ${gearItem.name}!`, "success");

  // Refresh displays (contractor cards show synergy indicators derived from
  // the player's equipped gear - window access avoids an import cycle)
  refreshAlpineInventory();
  window.refreshAlpineContractors?.();
  window.renderGearTab?.(); // gear-tab cards show equip state
  saveGameState();
  displayCharacter();
  return true;
}

// Unequip technique-specific gear
export function unequipTechniqueGear(techniqueId) {
  // Prevent unequipping gear for marketplace characters
  if (gameState.character.isMarketplace) {
    addNotification("Cannot modify equipment - character is on marketplace", "error");
    return false;
  }

  const gear = gameState.character.equippedTechniqueGear[techniqueId];
  if (!gear) return false;

  delete gameState.character.equippedTechniqueGear[techniqueId];
  addNotification(`Unequipped ${gear.name}!`, "info");

  // Refresh displays (incl. contractor synergy indicators)
  refreshAlpineInventory();
  window.refreshAlpineContractors?.();
  window.renderGearTab?.(); // gear-tab cards show equip state
  saveGameState();
  displayCharacter();
  return true;
}

function getAvailableSkillGear(techniqueId, skillKey) {
  const availableGear = Object.entries(gameState.character.inventory)
    .filter(([itemId, qty]) => qty > 0)
    .map(([itemId]) => {
      const item = findItem("id", itemId);
      return item;
    })
    .filter((item) => item && item.type === "techGear" && item.techniqueId === techniqueId);
  return availableGear;
}

// Show modal to select technique gear from inventory
export function showTechniqueGearSelector(techniqueId, skillKey) {
  // Get all techGear items from inventory
  const availableGear = getAvailableSkillGear(techniqueId, skillKey);
  if (availableGear.length === 0) {
    addNotification("No compatible gear in inventory", "warning");
    return;
  }

  // Create modal with gear options
  const gearOptions = availableGear
    .map(
      (gear) =>
        `<div class="flex items-center justify-between p-2 border border-gray-500 rounded mb-2 hover:bg-gray-700/50 cursor-pointer" onclick="equipTechniqueGear('${techniqueId}', '${
          gear.id
        }'); closeTechniqueGearSelector();">
            <div>
                <div class="font-bold text-white">${gear.name}</div>
                <div class="text-xs text-gray-400">${gear.icon} ${gear.short}</div>
                ${
                  gear.effects
                    ? `<div class="text-xs text-amber-300">Effects: ${Object.entries(gear.effects)
                        .map(([k, v]) => `${k}: +${v}`)
                        .join(", ")}</div>`
                    : ""
                }
            </div>
            <button class="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-bold border border-amber-500" onclick="event.stopPropagation(); equipTechniqueGear('${techniqueId}', '${
          gear.id
        }'); closeTechniqueGearSelector();">SELECT</button>
        </div>`
    )
    .join("");

  // Show modal
  const modal = document.getElementById("technique-gear-selector-modal");
  if (modal) {
    modal.remove();
  }

  const newModal = document.createElement("div");
  newModal.id = "technique-gear-selector-modal";
  newModal.className = "modal hidden";
  newModal.innerHTML = `
        <div class="modal-content max-w-md w-full">
            <div class="modal-header">
                <h3 class="modal-title">Select Gear for Technique</h3>
                <button class="close-btn" onclick="closeTechniqueGearSelector()">&times;</button>
            </div>
            <div class="modal-body">
                <div class="max-h-96 overflow-y-auto">
                    ${gearOptions}
                </div>
            </div>
        </div>
    `;

  document.body.appendChild(newModal);
  newModal.classList.remove("hidden");
}

// Close technique gear selector modal
export function closeTechniqueGearSelector() {
  const modal = document.getElementById("technique-gear-selector-modal");
  if (modal) {
    modal.classList.add("hidden");
  }
}

//
// Level-based card styling
//

/**
 * Get level-based card colors with continuous intensity increase
 * Lower levels have muted/darker tones, higher levels have brighter/more saturated tones
 * @param {number} level - Character or contractor level
 * @returns {Object} Object with Tailwind color classes
 */
export function getLevelBasedCardColors(level) {
  // Calculate color intensity per level
  // Each level gradually shifts from dark grays (800-900) toward lighter grays (100-300)
  // Level 1 starts at dark, increases by ~2 gray-scale points per level

  const grayScale = {
    100: "gray-100",
    200: "gray-200",
    300: "gray-300",
    400: "gray-400",
    500: "gray-500",
    600: "gray-600",
    700: "gray-700",
    800: "gray-800",
    900: "gray-900",
  };

  // Calculate shift amount: 2 points per level, capped at 80
  const shiftAmount = Math.min((level - 1) * 2, 80);

  // Helper function to get closest Tailwind gray class
  const getGray = (baseValue) => {
    const adjusted = Math.max(100, baseValue - shiftAmount);
    const rounded = Math.round(adjusted / 100) * 100;
    return grayScale[rounded];
  };

  return {
    cardBg: `from-${getGray(800)} to-${getGray(900)}`,
    cardBorder: `border-${getGray(600)}`,
    frameBorder: `border-${getGray(500)}`,
    headerBg: `from-${getGray(700)} to-${getGray(600)}`,
    headerBorder: `border-${getGray(500)}`,
  };
}

//
// Skill related functions
//

// (getPlayerSkillLevel removed 2026-07: dead code - zero callers, and it
// looked up artifact effects by skill id while artifacts key by effect id.
// Equipped-artifact bonuses now live in character-core's
// getSkillArtifactBonus/getUtilityArtifactBonus and apply at skill checks.)

// Display skills for selected faction
export function displaySkills() {
  const skillsSection = document.getElementById("skills-section");
  const pointsRemainingDiv = document.getElementById("points-remaining");
  const faction = config.factionSkills[gameState.selectedFaction];

  if (!faction) return;

  // Only show skills after character is created
  if (gameState.character) {
    skillsSection.classList.remove("hidden");
    skillsSection.classList.add("grid");
    skillsSection.innerHTML = "";

    Object.entries(faction.skills).forEach(([skillKey, skill]) => {
      const skillLevel = gameState.character.skills[skillKey] || 0;
      const skillPointCost = Math.max(1, Math.floor(skillLevel / 3) + 1);
      const currencyCost = calculateSkillCurrencyCost(skillLevel);
      const canAffordPoints = gameState.character.skillPoints >= skillPointCost;
      const canAffordCurrency = gameState.player.credits >= currencyCost;
      const canAfford = canAffordPoints && canAffordCurrency;

      // Concrete benefit: success chance per tier for rooms where this skill
      // is primary (gates and hazards have different targets), and the +5%
      // a level buys (until the 97% cap). The equipped skill artifact's flat
      // bonus folds in exactly, under the same 97% cap the resolver applies.
      const artifactPct = getSkillArtifactBonus(skillKey);
      const tierChances = [1, 2, 3].map(tier => {
        const tierData = config.raidTierData[tier];
        const gatePct = Math.min(97, Math.round(getSkillCheckChance(skillLevel, tierData.gateSkillLevel) * 100) + artifactPct);
        const hazardPct = Math.min(97, Math.round(getSkillCheckChance(skillLevel, tierData.hazardSkillLevel) * 100) + artifactPct);
        return `T${tier} <span class="text-gray-300">gates ${gatePct}% · hazards ${hazardPct}%</span>`;
      }).join(' &nbsp;|&nbsp; ');
      const atCap = getSkillCheckChance(skillLevel, config.raidTierData[3].hazardSkillLevel) * 100 + artifactPct >= 97;
      const nextLevelNote = atCap
        ? 'success chance at cap for current tiers'
        : 'next level: <span class="text-green-400 font-bold">+5%</span> on every room using this skill';

      const skillDiv = document.createElement("div");
      skillDiv.className = "skill-item";
      skillDiv.innerHTML = `
                <h4>${skill.name}</h4>
                <p class="text-gray-400 text-sm">${skill.description}</p>
                <div class="text-xs text-gray-400 mt-1" title="Your success chance per skill check in rooms where this skill is primary (before support/contractor bonuses)">
                    🎲 ${tierChances}
                    <span class="block mt-0.5">${nextLevelNote}</span>
                </div>
                <div class="skill-controls">
                    <span class="skill-level">Level: ${skillLevel}</span>
                    <span class="skill-cost">Cost: ${skillPointCost} point${skillPointCost > 1 ? "s" : ""} + ${currencyCost} credits</span>
                    <button class="skill-btn" onclick="upgradeSkill('${skillKey}')" ${!canAfford ? "disabled" : ""}>
                        Upgrade ${
                          !canAffordPoints
                            ? `(Need ${skillPointCost - gameState.character.skillPoints} points)`
                            : !canAffordCurrency
                            ? `(Need ${currencyCost - gameState.player.credits} credits)`
                            : ""
                        }
                    </button>
                </div>
            `;

      skillsSection.appendChild(skillDiv);
    });

    document.getElementById("points-remaining").textContent = gameState.character.skillPoints;
  } else {
    // Hide skills section during character creation
    skillsSection.classList.add("hidden");
    if (pointsRemainingDiv) {
      pointsRemainingDiv.classList.add("hidden");
    }
  }

  updateCreateButton();
}

// Upgrade skill
export function upgradeSkill(skillKey) {
  upgradeSkillCore(skillKey);

  displaySkills();
  displayCharacter();
  updateUI(); // Update credits display
  saveGameState();
}

// Buy the per-skill synergy unlock with skill points
export function unlockSkillSynergy(skillKey) {
  const result = unlockSkillSynergyCore(skillKey);
  if (!result.success) {
    const messages = {
      'insufficient-points': `⚠️ Need ${getSynergyUnlockConfig().cost} skill points to unlock synergies`,
      'already-unlocked': 'Synergies already unlocked for this skill',
      'not-revealed': `Synergies unlock at level ${getSynergyUnlockConfig().minLevel}`,
    };
    addNotification(messages[result.reason] || 'Cannot unlock synergies', 'warning');
    return;
  }

  addNotification(`⚡ ${getSkillDisplayName(skillKey)} synergies unlocked! Teammates' partner gear can now upgrade its events.`, 'success');
  displaySkills();
  displayCharacter();
  window.refreshAlpineContractors?.(); // card indicators depend on unlocks
  updateUI();
  saveGameState();
}

// Gain experience and handle leveling up
export function gainExperience(xpAmount) {
  if (!gameState.character) return;

  // Use core logic for XP gain and leveling
  gainExperienceCore(gameState.character, xpAmount);

  // Update UI to reflect changes
  updateUI();
  displayCharacter();

  // Update character selector to reflect XP changes
  if (typeof displayCharacterSelector !== "undefined") {
    displayCharacterSelector();
  }
}

// Handle character level up
export function levelUp() {
  const character = gameState.character;
  const hpBefore = character.maxHp;

  // Use core logic for leveling
  levelUpCore(character);

  const hpIncrease = character.maxHp - hpBefore;
  const skillPointsGained = 2 + Math.floor(character.level / 5); // Matches calculation in levelUpCore

  // Show level up notifications (UI only)
  addNotification(`🎉 Level Up! You are now level ${character.level}!`, "success");
  addNotification(`💪 +${hpIncrease} Max HP, +${skillPointsGained} Skill Points`, "success");

  // Log level up in raid log if active
  const logEntries = document.getElementById("log-entries");
  if (logEntries && logEntries.innerHTML !== "") {
    addLogEntry(`🎉 LEVEL UP! You reached level ${character.level}! (+${hpIncrease} HP, +${skillPointsGained} skill points)`, "success");
  }
}

//
// Avatar selection functions
//

// When the player clicks "New Set", this holds a fresh random root so the grid
// shows brand-new candidates instead of variants of their current avatar.
let galleryOverrideSeed = null;

// Generate avatar grid for selection
export function generateAvatarGrid() {
  const faction = gameState.character?.faction || "dominion";
  // Default: seeded candidates off the character's own seed so re-opening is
  // stable (root, then root#0..root#28 — current avatar first; "#k" suffix
  // stripped so picking a variant doesn't keep growing the seed). "New Set"
  // swaps in a fresh random root for a full page of brand-new faces.
  let seeds;
  if (galleryOverrideSeed) {
    seeds = Array.from({ length: 30 }, (_, k) => `${galleryOverrideSeed}#${k}`);
  } else {
    const root = String(getAvatarSeed(gameState.character || {})).replace(/#\d+$/, "");
    seeds = [root, ...Array.from({ length: 29 }, (_, k) => `${root}#${k}`)];
  }

  return seeds
    .map((seed) => {
      return `
            <div class="avatar-option" data-avatar-seed="${seed}">
                <div class="contractor-avatar-placeholder" data-avatar-seed="${seed}" data-avatar-faction="${faction}"></div>
            </div>
        `;
    })
    .join("");
}

// Setup avatar selection event listeners
export function setupAvatarSelection() {
  const changeAvatarBtn = document.getElementById("change-avatar-btn");
  const avatarSelector = document.getElementById("avatar-selector");
  const closeAvatarBtn = document.getElementById("close-avatar-selector");

  if (changeAvatarBtn) {
    changeAvatarBtn.addEventListener("click", () => {
      showAvatarSelector();
    });
  }

  if (closeAvatarBtn) {
    closeAvatarBtn.addEventListener("click", () => {
      hideAvatarSelector();
    });
  }

  // Close modal when clicking outside
  if (avatarSelector) {
    avatarSelector.addEventListener("click", (e) => {
      if (e.target === avatarSelector) {
        hideAvatarSelector();
      }
    });
  }

  wireAvatarOptions();

  // "New Set": swap the gallery in for a fresh random page of candidates.
  const refreshBtn = document.getElementById("avatar-gallery-refresh");
  if (refreshBtn) refreshBtn.addEventListener("click", () => {
    galleryOverrideSeed = `gallery_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const grid = document.getElementById("avatar-gallery-grid");
    if (grid) { grid.innerHTML = generateAvatarGrid(); wireAvatarOptions(); }
  });

  setupAvatarTabs();
}

// The raid team carousel caches each member's card HTML with the avatar seed
// baked in, so an avatar edit on the Characters page leaves the player's team
// card stale. Whenever that carousel is currently showing cards (team assembly
// or path planning - both display the team before a grid exists), rebuild it so
// the new look shows immediately. updateGridTeamDisplay self-guards on the DOM.
function refreshRaidTeamAvatar() {
  const teamDisplay = document.getElementById('grid-team-display');
  if (teamDisplay && teamDisplay.querySelector('[data-avatar-seed]')) {
    updateGridTeamDisplay();
  }
}

// Wire click-to-pick on gallery options. Idempotent (data-wired guard) so it can
// be re-run after regenerating the grid for a new set. A gallery pick reverts any
// custom avatar back to a seed.
function wireAvatarOptions() {
  document.querySelectorAll(".avatar-option").forEach((option) => {
    if (option.dataset.wired) return;
    option.dataset.wired = "1";
    option.addEventListener("click", () => {
      gameState.character.avatarSeed = option.dataset.avatarSeed;
      delete gameState.character.avatarFeatures;
      displayCharacter();
      hideAvatarSelector();
      saveGameState();
      if (typeof displayCharacterSelector !== "undefined") displayCharacterSelector();
      refreshRaidTeamAvatar();
    });
  });
}

// ---- Custom avatar creator (the "Custom" tab of the picker) --------------
// A working feature object the player edits live. Initialised from their current
// avatar: their saved custom features if any, else rolled from their seed.
let creatorFeatures = null;
let creatorAnimStop = null;

const avatarCreatorFaction = () => gameState.character?.faction || "dominion";

function initCreatorFeatures() {
  const c = gameState.character;
  creatorFeatures = c?.avatarFeatures
    ? { ...c.avatarFeatures }
    : featuresFromSeed(getAvatarSeed(c || {}), avatarCreatorFaction());
}

function stopCreatorPreviewAnim() {
  if (creatorAnimStop) { creatorAnimStop(); creatorAnimStop = null; }
}

// Animate the preview canvas off the LIVE creatorFeatures object (same idle loop
// the main card uses). Edits mutate that object in place and Randomize
// Object.assigns into it, so the running loop always reflects the current look.
function startCreatorPreviewAnim() {
  stopCreatorPreviewAnim();
  const canvas = document.getElementById("avatar-creator-canvas");
  if (canvas && creatorFeatures) {
    creatorAnimStop = mountAvatarAnimation(canvas, creatorFeatures, avatarCreatorFaction(), "creator");
  }
}

const acGet = (field) => (field.get ? field.get(creatorFeatures) : creatorFeatures[field.key]);
// Edits mutate creatorFeatures in place; the running preview loop shows them.
const acSet = (field, v) => { field.set ? field.set(creatorFeatures, v) : (creatorFeatures[field.key] = v); };
const acOpts = (field) => field.options.map((o) => (Array.isArray(o) ? o : [o, o]));

// One labelled control for a FEATURE_SCHEMA field (ported from avatar-lab.html).
function buildAvatarField(field) {
  const wrap = document.createElement("div");
  wrap.className = "cfield";
  const lab = document.createElement("label");
  lab.textContent = field.label;
  wrap.appendChild(lab);
  if (field.type === "bool") {
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = !!acGet(field);
    input.addEventListener("change", () => acSet(field, input.checked));
    wrap.appendChild(input);
  } else if (field.type === "int") {
    const input = document.createElement("input");
    input.type = "number";
    input.min = field.min; input.max = field.max; input.step = 1;
    input.value = acGet(field);
    input.addEventListener("input", () => { const v = parseInt(input.value, 10); if (!Number.isNaN(v)) acSet(field, v); });
    wrap.appendChild(input);
  } else if (field.type === "color") {
    const row = document.createElement("div"); row.className = "color-row";
    const input = document.createElement("input"); input.type = "color"; input.value = acGet(field) || "#888888";
    input.addEventListener("input", () => acSet(field, input.value));
    const presets = document.createElement("div"); presets.className = "presets";
    for (const cc of field.options || []) {
      const b = document.createElement("button"); b.type = "button"; b.style.background = cc; b.title = cc;
      b.addEventListener("click", () => { input.value = cc; acSet(field, cc); });
      presets.appendChild(b);
    }
    row.append(input, presets); wrap.appendChild(row);
  } else if (field.type === "enum") {
    const sel = document.createElement("select"); const opts = acOpts(field);
    opts.forEach(([label], i) => sel.add(new Option(label, String(i))));
    const idx = opts.findIndex(([, v]) => v === acGet(field));
    sel.value = String(idx < 0 ? 0 : idx);
    sel.addEventListener("change", () => acSet(field, opts[Number(sel.value)][1]));
    wrap.appendChild(sel);
  }
  return wrap;
}

// Build the grouped control grid (Face / Hair / Eyes / Accessories) for the
// current faction (drops faction-specific fields like the veil-only hood).
function renderAvatarCreatorControls() {
  const host = document.getElementById("avatar-creator-controls");
  if (!host) return;
  host.innerHTML = "";
  const faction = avatarCreatorFaction();
  const order = [], byGroup = new Map();
  for (const field of FEATURE_SCHEMA) {
    if (field.factions && !field.factions.includes(faction)) continue;
    if (!byGroup.has(field.group)) { byGroup.set(field.group, []); order.push(field.group); }
    byGroup.get(field.group).push(field);
  }
  for (const g of order) {
    const title = document.createElement("div"); title.className = "cgroup-title"; title.textContent = g;
    const grid = document.createElement("div"); grid.className = "creator-controls";
    for (const field of byGroup.get(g)) grid.appendChild(buildAvatarField(field));
    host.append(title, grid);
  }
}

// Wire the Gallery/Custom tabs + the creator's Randomize/Use buttons. Re-run
// each time the modal markup is rebuilt (setupAvatarSelection).
function setupAvatarTabs() {
  const tabs = document.querySelectorAll(".avatar-tab");
  const panes = {
    gallery: document.getElementById("avatar-pane-gallery"),
    custom: document.getElementById("avatar-pane-custom"),
  };
  tabs.forEach((tab) => tab.addEventListener("click", () => {
    const which = tab.dataset.avatarTab;
    tabs.forEach((t) => t.classList.toggle("active", t === tab));
    for (const [k, pane] of Object.entries(panes)) pane && pane.classList.toggle("active", k === which);
    if (which === "custom") {
      if (!creatorFeatures) initCreatorFeatures(); // first open this session; keeps edits across toggles
      renderAvatarCreatorControls();
      startCreatorPreviewAnim();
    } else {
      stopCreatorPreviewAnim(); // no need to animate a hidden preview
    }
  }));

  const randomBtn = document.getElementById("avatar-creator-random");
  if (randomBtn) randomBtn.addEventListener("click", () => {
    if (!creatorFeatures) initCreatorFeatures();
    // Mutate in place so the live preview loop keeps pointing at the same object.
    Object.assign(creatorFeatures, featuresFromSeed(`custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, avatarCreatorFaction()));
    renderAvatarCreatorControls();
    startCreatorPreviewAnim();
  });

  const applyBtn = document.getElementById("avatar-creator-apply");
  if (applyBtn) applyBtn.addEventListener("click", () => {
    if (!creatorFeatures) return;
    stopCreatorPreviewAnim();
    gameState.character.avatarFeatures = { ...creatorFeatures };
    displayCharacter();
    hideAvatarSelector();
    saveGameState();
    if (typeof displayCharacterSelector !== "undefined") displayCharacterSelector();
    refreshRaidTeamAvatar();
  });
}

// Show avatar selector modal. Always opens on the Gallery tab with a fresh
// character-based set (dropping any prior "New Set"), and resets the creator so
// the next Custom open rebuilds from the current avatar.
export function showAvatarSelector() {
  // Editing is locked while a raid is actively running (the button is also
  // disabled, but guard the action too - render timing isn't guaranteed).
  if (gameState.currentRaid?.raidInProgress) {
    addNotification("Can't edit your avatar while a raid is in progress", "info");
    return;
  }
  galleryOverrideSeed = null;
  const grid = document.getElementById("avatar-gallery-grid");
  if (grid) { grid.innerHTML = generateAvatarGrid(); wireAvatarOptions(); }
  document.querySelectorAll(".avatar-tab").forEach((t) => t.classList.toggle("active", t.dataset.avatarTab === "gallery"));
  document.getElementById("avatar-pane-gallery")?.classList.add("active");
  document.getElementById("avatar-pane-custom")?.classList.remove("active");
  stopCreatorPreviewAnim();
  creatorFeatures = null;
  const avatarSelector = document.getElementById("avatar-selector");
  if (avatarSelector) avatarSelector.classList.remove("hidden");
}

// Hide avatar selector modal (and tear down the creator preview loop).
export function hideAvatarSelector() {
  stopCreatorPreviewAnim();
  creatorFeatures = null;
  const avatarSelector = document.getElementById("avatar-selector");
  if (avatarSelector) {
    avatarSelector.classList.add("hidden");
  }
}

// ---------------------------------------------------------------------------
// Main character card: full card <-> drawer
//
// Collapsed, the card gives its ~240px column back to the Skills/Gear panes —
// the single biggest horizontal win available on this page for a tablet. What
// survives the collapse is the at-a-glance state (who, how healthy, what is
// equipped, what level) and the two actions that are not reachable anywhere
// else on the page. Renaming, the avatar editor and DELETE deliberately do not:
// a destructive control on a one-tap drawer is a mis-tap waiting to happen.
//
// Session-scoped, like the gear page's focus panel — a layout preference, not
// save data.
// ---------------------------------------------------------------------------
let characterCardCollapsed = false;

export function toggleCharacterCard() {
  characterCardCollapsed = !characterCardCollapsed;
  displayCharacter();
}

/** One drawer row: icon over value, or icon alone when value is null. */
function drawerChip(icon, value, title) {
  return `
    <div class="w-full flex flex-col items-center rounded border border-gray-700 bg-black/70 pt-2" title="${title}">
        <span class="text-sm leading-none">${icon}</span>
        ${value != null ? `<span class="text-[10px] font-bold leading-tight mt-0.5">${value}</span>` : ""}
    </div>`;
}

function renderCharacterDrawer({ faction, levelColors, isReadOnly, hasRaid }) {
  const character = gameState.character;
  const equippedGearCount = Object.values(character.equippedTechniqueGear || {}).filter(Boolean).length;
  const equippedArtifactCount = Object.values(character.equippedArtifacts || {}).filter(Boolean).length;
  const xpToNext = calculateXPToNextLevel(character.level);

  return `
    <div class="character-avatar-section">
        <div class="relative bg-gradient-to-b ${levelColors.headerBg} to-gray-900 border-4 ${
          isReadOnly ? "border-orange-500" : levelColors.cardBorder
        } rounded-xl shadow-lg w-16 flex flex-col items-center gap-1.5 p-1.5 h-[480px]">
            <button id="toggle-character-card-btn" onclick="toggleCharacterCard()"
                title="Expand ${character.name}" aria-expanded="false"
                class="w-full h-6 flex items-center justify-center rounded bg-black/40 border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 text-sm leading-none">▸</button>

            <div class="rounded-full w-7 h-7 flex items-center justify-center bg-black/30 border border-black text-sm flex-shrink-0"
                title="${faction.name}${isReadOnly ? " — on marketplace" : ""}">${faction.icon || "🏢"}</div>

            ${drawerChip("❤️", Math.floor(character.currentHp), `Health: ${Math.floor(character.currentHp)} / ${character.maxHp}`)}
            ${drawerChip("🧰", equippedGearCount, "Equipped skill gear")}
            ${drawerChip("🔮", equippedArtifactCount, "Equipped artifacts")}

            <div class="w-full rounded border-2 border-gray-500 bg-gradient-to-b from-gray-800 to-gray-900 py-1 text-center"
                title="Level ${character.level} — ${character.xp}/${xpToNext} XP">
                <div class="text-[9px] uppercase tracking-wide text-gray-400 leading-none">Lv</div>
                <div class="text-blue-300 font-bold text-sm leading-tight">${character.level}</div>
            </div>
            <div class="flex-grow"></div>
            ${
              isCommandRevealed()
                ? `<button id="promote-character-btn" onclick="openPromoteModal()"
                    class="w-full py-1 rounded text-xs font-bold transition-all duration-200 hover:shadow-lg"
                    style="border:1.5px solid #6d4fa3; background:#6d4fa3; color:#b18cff;"
                    title="Promote to Command — ${calculateCommandMarks(character).total} marks">🎖️</button>`
                : ""
            }
            <button id="contract-out-btn" ${hasRaid ? "disabled" : ""} class="${
              character.isMarketplace ? "bg-orange-600 hover:bg-orange-700" : "bg-yellow-600 hover:bg-yellow-700"
            } w-full text-white py-1 rounded text-xs font-bold transition-all duration-200" title="${
              character.isMarketplace ? "Withdraw from Marketplace" : "Contract Out to Marketplace"
            }">${character.isMarketplace ? "📤" : "💼"}</button>
            
            <button id="delete-character-btn" class="w-full bg-red-600 hover:bg-red-700 text-white py-1 rounded text-xs font-bold transition-all duration-200 ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}" 
              title="${isReadOnly ? "Cannot delete - character on marketplace" : "Delete Character"}" ${isReadOnly ? "disabled" : ""}>🗑️</button>
        </div>
    </div>`;
}

//
// Main character display function
//

// Display character info
export function displayCharacter() {
  // Update character selector first
  if (gameState.characters.length > 0) {
    displayCharacterSelector();
  }

  // Update inventory header
  updateInventoryCharacterHeader();
  const faction = config.factionSkills[gameState.character.faction];

  // Check if character is in read-only mode (on marketplace)
  const isReadOnly = gameState.character.isMarketplace;

  // Update header background when character is displayed
  updateHeaderBackground(gameState.character.faction);

  // One skill card at a time — the strip below picks which, and carries that
  // skill's level and upgrade button.
  const shownSkill = activeSkillTab(SKILL_TAB_GROUP, gameState.character.faction);

  let skillsHtml = "";
  Object.entries(gameState.character.skills).filter(([skillKey]) => skillKey === shownSkill).forEach(([skillKey, level]) => {
    const skillShort = faction.skills[skillKey].short;
    const skillPointCost = skillPointCostFor(level);
    const canAfford = gameState.character.skillPoints >= skillPointCost &&
      gameState.player.credits >= calculateSkillCurrencyCost(level) &&
      !isReadOnly;

    // The card's old header row is gone — the tab strip above already names the
    // skill. Its level and upgrade button moved down here, beside the synergy
    // control: both are "spend points on this skill", so they belong together.
    skillsHtml += `
            <div class="skill-card relative flex flex-col justify-items-stretch border-2 border-gray-600/90 rounded-lg shadow-md bg-${gameState.character.faction}-bright">
                <!-- Header Banner -->
                <div class="relative text-gray-100 backdrop-blur-lg bg-black/60 rounded-lg rounded-b-none">
                    <div class="flex flex-col items-end mt-2 mx-2 rounded-lg bg-black/65">
                        <!-- Synergy unlock, then level + upgrade. This row is NOT
                             gated on the synergy reveal level: it now carries the
                             only way to spend skill points, and the synergy
                             control below already hides itself before then. -->
                        <div class="flex items-center gap-2 w-full p-2">
                            <div class="flex-grow text-sm">
                              <p class="text-gray-100 leading-relaxed">${skillShort}</p>
                            </div>
                            ${(() => {
                              // Per-skill synergy unlock: appears at the reveal
                              // level, bought with skill points, then permanent
                              if (!isSynergyRevealed() || isReadOnly) return "";
                              if (isSynergyUnlocked(skillKey)) {
                                return `<span class="px-2 py-1 rounded text-sm font-bold bg-amber-900/60 text-amber-300 border border-amber-700 whitespace-nowrap" title="Synergy variants can fire on this skill's events, and its equipped gear can partner teammates' events">⚡ Synergies</span>`;
                              }
                              const cfg = getSynergyUnlockConfig();
                              const canUnlock = gameState.character.skillPoints >= cfg.cost;
                              return `<button class="synergies-btn min-w-auto px-2 py-1 rounded whitespace-nowrap transition-all duration-200 text-sm font-bold ${
                                canUnlock
                                  ? "bg-blue-600 hover:bg-blue-500 text-white hover:shadow-lg border border-blue-400"
                                  : "bg-white/10 text-gray-400 cursor-not-allowed border border-gray-700"
                              }" onclick="unlockSkillSynergy('${skillKey}')" ${!canUnlock ? "disabled" : ""} title="Unlock synergy variants for this skill's events and let its gear partner teammates' events">
                                  🔓 Synergies (${cfg.cost} pts)
                              </button>`;
                            })()}
                            <div class="flex flex-shrink-0">
                                <div class="bg-amber-500 text-black px-2 py-1 rounded-l-md text-sm font-bold inline-block border border-amber-500">Lv.${level}</div>
                                <button class="skill-upgrade-btn min-w-auto px-2 py-1 rounded-r-md transition-all duration-200 text-sm font-bold ${
                                  canAfford
                                    ? "bg-blue-600 hover:bg-blue-500 text-white hover:shadow-lg border border-blue-400"
                                    : "bg-white/10 text-black cursor-not-allowed border border-gray-700"
                                }" onclick="upgradeSkill('${skillKey}')" ${!canAfford ? "disabled" : ""}>
                                    ${isReadOnly ? "🚫" : `▲ ${skillPointCost}pt${skillPointCost > 1 ? "s" : ""}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Card Content -->
                <div class="relative flex-grow text-[0.9rem] p-2 bg-black/60 backdrop-blur-[2px] border-b rounded-b-lg border-transparent">
                    <!-- No fixed height: the h-12 that used to level card tops
                         across a grid row costs two blank lines now that one
                         card renders at full width. -->

                    <!-- Skill Techniques Tabs -->
                    ${(() => {
                      const factionTechniques = config.skillTechniques[gameState.character.faction] || [];
                      const techniques = factionTechniques.filter((t) => t.skill === skillKey);
                      if (techniques.length === 0) return "";

                      const tabId = `techniques-${skillKey}`;

                      // Get the saved tab selection for this skill, default to 0
                      const selectedTabIndex = gameState.character.techniqueTabSelections ? gameState.character.techniqueTabSelections[tabId] ?? 0 : 0;

                      // Tab buttons (icons)
                      const tabButtons = techniques
                        .map(
                          (t, idx) =>
                            `<button
                                class="flex-1 technique-tab px-2 py-[2px] rounded-lg rounded-bl-none rounded-br-none text-lg transition-colors ${
                                  idx === selectedTabIndex ? "bg-black/65" : "bg-gray-500/60 hover:bg-gray-900/60"
                                }"
                                onclick="window.switchTechniqueTab('${tabId}', ${idx})"
                                data-tab-group="${tabId}"
                                data-tab-index="${idx}"
                                title="${t.name}">
                                ${t.icon}
                            </button>`
                        )
                        .join("");

                      // Tab content panels
                      const tabPanels = techniques
                        .map((t, idx) => {
                          // Progressive disclosure: synergies are invisible
                          // below the reveal level, a locked teaser until the
                          // skill's unlock is bought, then fully detailed.
                          // Each synergy is unlocked by one specific cross-faction
                          // device: the partner technique (from this technique's
                          // event variants) whose skill matches the synergy target.
                          // One synergy per column (gear management lives on the
                          // Gear tab now, so the old gear column is gone).
                          let synergyBlocks = ["", ""];
                          if (isSynergyRevealed() && !isSynergyUnlocked(skillKey)) {
                            synergyBlocks[0] = `<div class="text-gray-400 text-sm leading-tight mt-1" title="Synergy variants: teammates' cross-faction gear upgrades this skill's events for bonus salvage">🔒 Synergies — unlock on the skill card above (${getSynergyUnlockConfig().cost} pts)</div>`;
                          } else if (isSynergyRevealed() && isSynergyUnlocked(skillKey)) {
                            const allDevices = [...config.itemsEpicDeviceDominion, ...config.itemsEpicDeviceWilders, ...config.itemsEpicDeviceVeil];
                            const partnerTechniques = [...getSynergyPartnersForTechnique(t.id)];
                            synergyBlocks = t.synergies
                              .map((s, idex) => {
                                const partnerTech = partnerTechniques.find(p => getTechniqueSkill(p) === s.targetSkill);
                                const device = partnerTech ? allDevices.find(d => d.techniqueId === partnerTech) : null;
                                const gearLine = device
                                  ? `<div class="text-[11.5px] text-amber-300/90 leading-tight mb-3" title="A teammate with this equipped unlocks the synergy variant (bonus salvage)">${device.icon || '⚙️'} ${device.name}</div>`
                                  : '';
                                return `<div class="text-blue-300 leading-tight mb-1 font-bold">
                                    <span>${config.factionSkills[s.targetFaction].name} Synergy</span> - 
                                    <span class="text-green-400">${config.factionSkills[s.targetFaction].skills[s.targetSkill].name}</span>
                                </div>
                                <div class="text-sm/4 ${device ? 'mb-1' : 'mb-3'}">${s.effect}</div>
                                ${gearLine}`;
                              });
                          }

                          return `
                                <div class="technique-panel ${idx === selectedTabIndex ? "" : "hidden"}" data-tab-group="${tabId}" data-panel-index="${idx}">
                                    <div class="text-base font-bold text-gray-200 mb-1 leading-tight p-1 px-2 bg-black/50">Tech: ${t.name}</div>
                                    <div class="">
                                        <div class="text-gray-200 my-3 leading-tight px-2">${t.description}</div>
                                        <div class="flex ${isSynergyRevealed() ? '' : 'hidden'}">
                                            <div class="flex-1 text-gray-200 p-1 px-2">${synergyBlocks[0] || ""}</div>
                                            <div class="flex-1 text-gray-200 p-1 border-l border-gray-600 px-2">${synergyBlocks[1] || ""}</div>
                                        </div>
                                    </div>
                                </div>
                            `;
                        })
                        .join("");

                      return `
                            <div>
                                <div class="flex gap-1">
                                    ${tabButtons}
                                </div>
                                <div class="technique-content rounded-lg rounded-t-none overflow-y-auto overflow-x-hidden bg-black/30">
                                    ${tabPanels}
                                </div>
                            </div>
                        `;
                    })()}
                </div>
            </div>
        `;
  });

  const hasRaid = gameState.currentRaid && gameState.currentRaid.raidOwnerId == gameState.character.id;
  // A raid actively running (not merely planned) locks name/avatar edits.
  const raidRunning = !!gameState.currentRaid?.raidInProgress;
  const xpToNext = calculateXPToNextLevel(gameState.character.level);
  const xpPercentage = (gameState.character.xp / xpToNext) * 100;
  const overlayBg = "bg-black bg-opacity-70";
  const overlayBorder = "border-gray-500";
  const overlayText = "text-gray-400";

  // Get level-based card colors
  const levelColors = getLevelBasedCardColors(gameState.character.level || 1);

  // Equipped-loadout badge counts (mirror the HP badge on the opposite corner)
  const equippedGearCount = Object.values(gameState.character.equippedTechniqueGear || {}).filter(Boolean).length;
  const equippedArtifactCount = Object.values(gameState.character.equippedArtifacts || {}).filter(Boolean).length;

  // The 2-column shell + sub-tab bar are static in index.html; render the
  // left card column and the Skills pane into their mounts (Inventory/Gear
  // panes are static and toggled by the sub-tabs).
  if (characterCardCollapsed) {
    // The drawer has no portrait to animate, and nothing else will arrive to
    // displace the running loop — stop it explicitly.
    stopMainAvatar();
    document.getElementById("char-card-mount").innerHTML =
      renderCharacterDrawer({ faction, levelColors, isReadOnly, hasRaid });
  } else {
  // The full card's template keeps its original indentation rather than being
  // shifted one level in — re-flowing 150 untouched lines to satisfy the new
  // brace would bury this change in the diff and blame.
  document.getElementById("char-card-mount").innerHTML = `
                <div class="character-avatar-section">
                    <div class="relative bg-gradient-to-b ${levelColors.cardBg} border-4 ${levelColors.cardBorder} rounded-xl shadow-lg ${isCommandRevealed() ? `h-[480px]`: `h-[450px]`} w-60 overflow-hidden">
                        <!-- Card Border Frame -->
                        <div class="absolute inset-1 border-2 ${levelColors.frameBorder} rounded-lg"></div>

                        <!-- Header Banner -->
                        <div class="relative ${
                          isReadOnly
                            ? "bg-gradient-to-r from-yellow-600 to-orange-600 border-b-2 border-transparent"
                            : `bg-gradient-to-r ${levelColors.headerBg} border-b-2 ${levelColors.headerBorder}`
                        } text-gray-100 px-1 py-1 text-left h-8 flex items-center justify-between">
                            <div class="flex items-center gap-1 min-w-0">
                                <button id="toggle-character-card-btn" onclick="toggleCharacterCard()"
                                    title="Collapse to a drawer" aria-expanded="true"
                                    class="w-12 h-6 flex items-center justify-center rounded bg-black/40 border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 text-sm leading-none">◂</button>
                                <span class="text-s font-bold text-black truncate">${isReadOnly ? "On Marketplace" : ""}</span>
                            </div>
                            <div class="bg-black bg-opacity-30 border border-black rounded-full w-6 h-6 flex items-center justify-center text-sm" title="${
                              faction.name
                            }">${faction.icon || "🏢"}</div>
                        </div>

                        <!-- Card Content -->
                        <div class="relative p-3 bg-gradient-to-b ${levelColors.cardBg}" style="height: calc(100% - 33px);">
                            <!-- Title Section with Rename Button -->
                            <div class="h-4 flex items-center">
                                <h4 class="font-bold text-white text-base leading-tight">
                                    ${gameState.character.name}
                                </h4>
                                ${
                                  !isReadOnly
                                    ? `
                                <button id="rename-character-btn" class="px-1 transition-colors text-sm ${raidRunning ? "text-gray-500 opacity-40 cursor-not-allowed" : "text-purple-300 hover:text-purple-100 cursor-pointer"}" title="${raidRunning ? "Can't edit during a raid" : "Edit Character"}" ${raidRunning ? "disabled" : ""}>
                                    ✏️
                                </button>
                                `
                                    : ""
                                }
                            </div>

                            <div class="h-4 mb-2">
                                <h3 class="font-italic text-gray-400 text-xs leading-tight">
                                    ${gameState.character.title ? gameState.character.title : ""}
                                </h3>
                            </div>

                            <!-- Avatar Area -->
                            <div class="relative bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-500 rounded-lg h-[200px] w-full mb-3 overflow-hidden group cursor-pointer">
                                <!-- Avatar -->
                                <div class="absolute inset-0 flex items-center justify-center">
                                    <div class="contractor-avatar-placeholder transform group-hover:scale-110 transition-transform duration-200" data-avatar-main="1" ${avatarDataAttrs(gameState.character)}></div>
                                </div>
                                <!-- Change Avatar Button -->
                                ${
                                  !isReadOnly
                                    ? `
                                <button id="change-avatar-btn" class="absolute top-1 right-1 p-1 rounded text-xs transition-all duration-200 ${raidRunning ? "bg-black bg-opacity-40 text-gray-400 cursor-not-allowed" : "bg-black bg-opacity-70 hover:bg-opacity-90 hover:scale-110 text-white"}" title="${raidRunning ? "Can't edit during a raid" : "Edit avatar"}" ${raidRunning ? "disabled" : ""}>
                                    ✏️
                                </button>
                                `
                                    : ""
                                }
                                <!-- HP Status (like contractor cards) -->
                                <div class="absolute bottom-1 left-1 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs border border-gray-700">
                                    ❤️${Math.floor(gameState.character.currentHp)}
                                </div>

                                <!-- Equipped loadout badges (opposite the HP badge): skill gear then artifacts -->
                                <div class="absolute bottom-1 right-1 flex gap-1">
                                    <div class="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs border border-gray-700" title="Equipped skill gear">🧰${equippedGearCount}</div>
                                    <div class="bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs border border-gray-700" title="Equipped artifacts">🔮${equippedArtifactCount}</div>
                                </div>

                                <!-- Quote Overlay at Bottom (hidden by default, shown on hover) -->
                                <div class="${
                                  gameState.character.quote ? "" : "hidden"
                                } absolute bottom-0 left-0 right-0 ${overlayBg} p-2 border-t ${overlayBorder} transform translate-y-full group-hover:translate-y-0 transition-transform duration-200 z-10">
                                    <p class="${overlayText} text-xs leading-tight">${gameState.character.quote ? gameState.character.quote : ""}</p>
                                </div>
                            </div>

                            <!-- Character Info Section (Level and XP only) -->
                            <div class="mt-2 text-xs space-y-3 mb-3 bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-500 rounded-lg p-2">
                                <div class="text-center space-y-3">
                                    <div class="text-blue-300 font-semibold text-base/4">Level ${gameState.character.level}</div>
                                    <div class="bg-gray-700 rounded-lg h-5 overflow-hidden relative">
                                        <div class="bg-gradient-to-r from-blue-500 to-blue-400 h-full transition-all duration-300" style="width: ${xpPercentage}%;"></div>
                                        <div class="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold">
                                            ${gameState.character.xp}/${xpToNext}
                                        </div>
                                    </div>
                                    <div class="text-xs text-gray-400 text-center" title="What the next level-up grants">
                                        Next Lvl: <span class="text-game-success font-semibold">+${(() => {
                                          const cfg = config.progressionScaling.skillPointsPerLevel;
                                          const nextLevel = gameState.character.level + 1;
                                          return cfg.base + Math.floor(nextLevel / cfg.levelDivisor) * cfg.increment;
                                        })()} SP</span> · <span class="text-red-300 font-semibold tracking-tighter">+${hpGainForLevel(gameState.character.level + 1)} HP</span>
                                    </div>
                                    ${
                                      isCommandRevealed()
                                        ? `<div class="text-xs font-semibold" style="color:#b18cff" title="Command Marks if promoted now">🎖️ Command value: ${calculateCommandMarks(gameState.character).total}</div>`
                                        : ""
                                    }
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="mt-2 flex justify-between">
                                <button id="delete-character-btn" class="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-bold transition-all duration-200 ${
                                  isReadOnly ? "opacity-50 cursor-not-allowed" : ""
                                }" title="${isReadOnly ? "Cannot delete - character on marketplace" : "Delete Character"}" ${isReadOnly ? "disabled" : ""}>
                                    🗑️
                                </button>
                                ${
                                  isCommandRevealed()
                                    ? `<button id="promote-character-btn" onclick="openPromoteModal()" class="px-2 py-1 rounded text-xs font-bold transition-all duration-200 hover:shadow-lg" style="border:1.5px solid #6d4fa3; background: #6d4fa3; color:#b18cff;" title="Promote to Command">
                                    🎖️
                                </button>`
                                    : ""
                                }
                                <button id="contract-out-btn" ${hasRaid ? "disabled" : ""} class="${
    gameState.character.isMarketplace ? "bg-orange-600 hover:bg-orange-700" : "bg-yellow-600 hover:bg-yellow-700"
  } text-white px-2 py-1 rounded text-xs font-bold transition-all duration-200" title="${
    gameState.character.isMarketplace ? "Withdraw from Marketplace" : "Contract Out to Marketplace"
  }">
                                    ${gameState.character.isMarketplace ? "📤" : "💼"}
                                </button>
                            </div>

                        </div>
                    </div>

                    <!-- Equipment Section (Under Character Card) -->
                    <div class="character-equipment-section bg-white/5 border-4 border-gray-600 hidden">
                        <div class="equipment-slots flex flex-col gap-4">
                            <!-- Skill Enhancement Slot -->
                            <div class="equipment-slot-wrapper flex flex-col items-center">
                                ${
                                  gameState.character.equippedArtifacts.skillSlot
                                    ? createInventoryCard(gameState.character.equippedArtifacts.skillSlot)
                                    : '<div class="empty-slot bg-amber-800/20 border-2 border-dashed border-amber-600 rounded-xl h-[180px] w-[160px] flex items-center justify-center text-gray-400 text-sm text-center">No skill artifact equipped</div>'
                                }
                            </div>

                            <!-- Utility Enhancement Slot -->
                            <div class="equipment-slot-wrapper flex flex-col items-center">
                                ${
                                  gameState.character.equippedArtifacts.utilitySlot
                                    ? createInventoryCard(gameState.character.equippedArtifacts.utilitySlot)
                                    : '<div class="empty-slot bg-amber-800/20 border-2 border-dashed border-amber-600 rounded-xl h-[180px] w-[160px] flex items-center justify-center text-gray-400 text-sm text-center">No utility artifact equipped</div>'
                                }
                            </div>
                        </div>
                    </div>
                </div>
        `;
  }

  document.getElementById("char-skills-pane").innerHTML = `
                <div class="min-h-[415px] bg-white/5 border-4 border-white/20 rounded-lg rounded-tl-none p-2">
                    <div class="flex items-start">
                        <div class="text-lg font-semibold text-white mr-2 flex-grow">Faction Skills: ${faction.name} ${faction.icon}</div>
                        <div class="bg-amber-500 text-black text-sm px-3 py-1 rounded font-bold place-content-end">
                            ${gameState.character.skillPoints} Points Available
                        </div>
                    </div>
                    
                    <div class="text-gray-300 text-sm mt-2">${faction.description}</div>
                    ${faction.doctrine ? `<div class="text-gray-400 text-sm italic mt-1 hidden">${faction.doctrine}</div>` : ""}

                    <div class="skills-container mt-2">
                        ${skillTabStrip(SKILL_TAB_GROUP, gameState.character.faction)}
                        <div id="skills-grid" class="pr-4">
                            ${skillsHtml}
                        </div>
                    </div>
                </div>

        <div id="avatar-selector" class="modal hidden">
            <div class="modal-content max-w-4xl w-full max-h-[80vh] flex flex-col">
                <div class="modal-header">
                    <h3 class="modal-title">Choose Your Avatar</h3>
                    <button id="close-avatar-selector" class="close-btn">&times;</button>
                </div>
                <div class="avatar-tabs">
                    <button class="avatar-tab active" data-avatar-tab="gallery">Gallery</button>
                    <button class="avatar-tab" data-avatar-tab="custom">Custom</button>
                </div>
                <div class="modal-body flex-1 overflow-hidden flex flex-col">
                    <div id="avatar-pane-gallery" class="avatar-pane active">
                        <div class="avatar-gallery-toolbar">
                            <button id="avatar-gallery-refresh" class="ac-btn ac-btn-inline">🎲 New Set</button>
                        </div>
                        <div id="avatar-gallery-grid" class="avatar-modal-grid grid grid-cols-6 gap-1 p-2">
                            ${generateAvatarGrid()}
                        </div>
                    </div>
                    <div id="avatar-pane-custom" class="avatar-pane avatar-creator">
                        <div class="ac-preview">
                            <canvas id="avatar-creator-canvas" width="64" height="64"></canvas>
                            <button id="avatar-creator-random" class="ac-btn">🎲 Randomize</button>
                            <button id="avatar-creator-apply" class="ac-btn ac-btn-primary">✔ Use Avatar</button>
                        </div>
                        <div id="avatar-creator-controls" class="ac-controls"></div>
                    </div>
                </div>
            </div>
        </div>
    `;

  // Show the active sub-tab pane (Skills / Inventory / Gear) after re-render
  applyCharSubTab();

  // Add event listeners for avatar selection
  setupAvatarSelection();

  addCharacterRenameButtonListener();

  // Add event listener for craft artifacts button
  const craftBtn = document.getElementById("craft-artifacts-btn");
  if (craftBtn) {
    craftBtn.addEventListener("click", openCraftingModal);
  }

  // Add event listener for delete character button
  const deleteBtn = document.getElementById("delete-character-btn");
  if (deleteBtn) {
    deleteBtn.addEventListener("click", showDeleteCharacterConfirmation);
  }

  // Add event listener for contract out button
  const contractOutBtn = document.getElementById("contract-out-btn");
  if (contractOutBtn) {
    contractOutBtn.addEventListener("click", () => {
      if (!gameState.character || !gameState.activeCharacterId) {
        addNotification("No active character selected", "error");
        return;
      }

      if (gameState.character.isMarketplace) {
        // Withdraw from marketplace
        withdrawFromMarketplace(gameState.activeCharacterId);
        return;
      }

      // Cannot contract out character who owns an active raid
      if (
        gameState.currentRaid.raidOwnerId === gameState.activeCharacterId &&
        (gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted || gameState.currentRaid.currentRaidGrid)
      ) {
        addNotification("Cannot contract out character who has an active raid", "error");
        return;
      }

      showContractOutModal();
    });
  }
}

//
// Contractor related functions
//

// Contractor XP/leveling for a hired contractor happens in-memory via the
// *Core helpers during a raid; the contractor's owner receives the actual
// progression through the payments flow (see recordContractorPayments /
// checkAndApplyPendingPayments). The former gainContractorExperience/
// levelUpContractor wrappers wrote directly to the contractor's DB row — a
// cross-owner write incompatible with ownership enforcement — and were unused,
// so they were removed.

//
// Character creation and management functions
//

// Update create character button
export function updateCreateButton() {
  const btn = document.getElementById("create-character");
  const nameInput = document.getElementById("character-name-create");
  const validationMessage = document.getElementById("name-validation-message");

  // Check if faction is selected
  const hasFaction = gameState.selectedFaction;

  // Check if name is valid
  const hasValidName = nameInput && nameInput.value.trim().length >= 2;

  // Check if name validation passed (no error message showing)
  const nameValidationPassed =
    validationMessage && (validationMessage.classList.contains("hidden") || validationMessage.classList.contains("text-game-success"));

  btn.disabled = !hasFaction || !hasValidName || !nameValidationPassed;
}

// Multi-character management functions
export function createNewCharacter(characterData) {
  // Generate unique character ID
  const characterId = `char_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const newCharacter = {
    id: characterId,
    name: characterData.name,
    faction: characterData.faction,
    skills: characterData.skills || {},
    skillPoints: characterData.skillPoints || 3,
    maxHp: characterData.maxHp || 100,
    currentHp: characterData.currentHp || 100,
    level: characterData.level || 1,
    xp: characterData.xp || 0,
    avatar: characterData.avatar, // legacy sprite class, kept as fallback
    avatarSeed: characterData.avatarSeed || characterId, // procedural portrait seed (stable id)
    title: characterData.title || null,
    quote: characterData.quote || null,
    multiplayerId: characterData.multiplayerId || null, // Will be set after registering with multiplayer
    ownerToken: characterData.ownerToken || null, // Server-issued ownership token (ServerAuthoritySaveSecurity.md §2)
    createdAt: Date.now(),
    isMarketplace: false, // Whether this character is contracted out
    marketplaceData: null, // Marketplace listing info
    inventory: {}, // Character-specific inventory
    artifacts: [], // Character-specific artifacts
    equippedArtifacts: {
      skillSlot: null,
      utilitySlot: null,
    },
    equippedTechniqueGear: {},
    techniqueTabSelections: {}, // Remembers selected technique tabs per skill
    lifetime: { t3Wins: 0, artifactsCrafted: 0, artifactsUnlocked: 0 }, // Feeds the Command Marks formula
  };

  // Command layer perks for the new recruit
  const startLevel = getUpgradeEffect("officer-academy", 0);
  if (startLevel > 1) {
    applyStartingLevel(newCharacter, startLevel);
    addNotification(`🏛️ Officer Academy: ${newCharacter.name} starts at level ${newCharacter.level}`, "success");
  }
  const conditioning = getUpgradeEffect("field-conditioning", 0);
  if (conditioning > 0) {
    newCharacter.maxHp += conditioning;
    newCharacter.currentHp += conditioning;
    addNotification(`🩺 Field Conditioning: ${newCharacter.name} starts with +${conditioning} max HP`, "success");
  }
  const vaultGranted = claimVaultForNewCharacter(newCharacter);
  const vaultKinds = Object.keys(vaultGranted).length;
  if (vaultKinds > 0) {
    addNotification(`🏦 Guild Vault: ${vaultKinds} material type${vaultKinds === 1 ? "" : "s"} delivered from the last tour`, "success");
  }

  // Add to characters array
  gameState.characters.push(newCharacter);

  // Always set newly created character as active
  switchToCharacter(characterId);

  return newCharacter;
}

// Show delete character confirmation dialog
export function showDeleteCharacterConfirmation() {
  const confirmDelete = confirm(
    "⚠️ DELETE CHARACTER ⚠️\n\n" +
      "This will permanently delete your character and reset ALL progress including:\n" +
      "• Character level and skills\n" +
      "• All inventory items and credits\n" +
      "• Raid progress and contractors\n" +
      "• Equipped artifacts and technique gear\n\n" +
      "This action CANNOT be undone!\n\n" +
      "Type 'DELETE' to confirm:"
  );

  if (confirmDelete) {
    const finalConfirm = prompt("Type 'DELETE' to permanently delete your character:");
    if (finalConfirm === "DELETE") {
      deleteCharacter();
    } else {
      addNotification("Character deletion cancelled", "info");
    }
  }
}

// Delete character and reset game state
export async function deleteCharacter(characterId = gameState.activeCharacterId) {
  const character = gameState.characters.find((c) => c.id === characterId);
  if (!character) {
    console.error("Character not found:", characterId);
    return false;
  }

  // Cannot delete character who owns an active raid
  if (
    gameState.currentRaid.raidOwnerId === characterId &&
    (gameState.currentRaid.currentRaidGrid != null || gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted)
  ) {
    addNotification(`Cannot delete ${character.name} - they have an active raid in progress`, "error");
    return false;
  }

  // Remove from marketplace first if needed
  if (character.isMarketplace) {
    removeFromMarketplace(characterId);
  }

  // Remove from multiplayer if applicable
  if (character.multiplayerId) {
    multiplayer.deleteOwnCharacter(character.multiplayerId);
  }

  // Remove from characters array
  const index = gameState.characters.findIndex((c) => c.id === characterId);
  debug(CH.ui, "Deleting character:", characterId, character.name, index);
  if (index !== -1) {
    gameState.characters.splice(index, 1);
  }

  // Clear all character-related game state
  gameState.character = null;
  gameState.selectedFaction = null;
  // Credits are shared between all characters - don't reset.
  // The contractor POOL is account-level too (NPCs with negative ids, shared by
  // every character), so deleting one character must not wipe it. It used to,
  // which left the account unable to hire for the rest of the session — and made
  // Foreman's Ledger report "No suitable contractors available for this route",
  // reading as a broken upgrade rather than an empty pool. It only looked benign
  // because a reload regenerates the pool.
  // The hired/reserved lists ARE raid-scoped and do belong to the deleted
  // character's run, so those are still cleared.
  gameState.currentRaid.hiredContractors = [];
  gameState.currentRaid.reservedContractors = [];

  // Clear any active raids
  /*
    gameState.currentRaid.raidInProgress = null;
    gameState.currentRaid.raidCompleted = false;
    gameState.currentRaid.currentRaidGrid = null;
    gameState.currentRaid.selectedPath = [];
    gameState.currentRaid.selectedRegion = null;
    gameState.currentRaid.selectedTier = null;
    gameState.currentRaid.raidOwnerId = null;
    gameState.currentRaid.completedRaidInfo = null;
    */

  // Reset energy
  //gameState.energy = 100;
  //gameState.maxEnergy = 100;

  // Refresh Alpine store since we cleared inventory and artifacts
  if (window.refreshAlpineInventory) {
    window.refreshAlpineInventory();
  }

  // Save the reset state
  saveGameState();

  // Hide any active raid interface
  document.getElementById("raid-grid-section").classList.add("hidden");
  document.getElementById("raid-team").classList.add("hidden");
  document.getElementById("raid-setup").classList.remove("hidden");
  document.getElementById("raid-active").classList.add("hidden");

  // Reset UI
  updateUI();
  displaySkills();

  // Update tab visibility
  updateTabVisibility();

  // If this was the active character, switch to another one
  if (gameState.activeCharacterId === characterId) {
    const availableChars = getAvailableCharacters();
    if (availableChars.length > 0) {
      switchToCharacter(availableChars[0].id);
    } else {
      // Show character creator
      document.getElementById("character-creator").classList.remove("hidden");
      document.getElementById("character-display").classList.add("hidden");
      resetCharacterCreationForm();
    }
  }

  addNotification("🔥 Character deleted!", "success");
  return true;
}

// Post-promotion flow (called by command.js via window to avoid a module cycle):
// the active slot was freed by promoteCharacterCore - switch to another
// character if one exists, otherwise open the creator for the next tour.
export function showCharacterScreenAfterPromotion() {
  const availableChars = getAvailableCharacters();
  if (availableChars.length > 0) {
    switchToCharacter(availableChars[0].id);
  } else {
    document.getElementById("character-creator").classList.remove("hidden");
    document.getElementById("character-display").classList.add("hidden");
    resetCharacterCreationForm();
    updateFactionAvailability();
  }
  updateUI();
  updateTabVisibility();
  // Land the player on the character tab (creator or roster)
  const characterTabBtn = document.querySelector('[data-tab="character"]');
  if (characterTabBtn && !characterTabBtn.classList.contains("active")) {
    characterTabBtn.click();
  }
}
if (typeof window !== "undefined") {
  window.showCharacterScreenAfterPromotion = showCharacterScreenAfterPromotion;
}

// Reset character creation form to initial state
export function resetCharacterCreationForm() {
  // Clear character name input
  const nameInput = document.getElementById("character-name-create");
  debug(CH.ui, "Resetting character creation form, name input:", nameInput);
  if (nameInput) {
    nameInput.value = "";
  }

  // Clear validation message
  const validationMessage = document.getElementById("name-validation-message");
  debug(CH.ui, "Resetting character creation validation message:", validationMessage);
  if (validationMessage) {
    validationMessage.textContent = "";
    validationMessage.className = "mt-2 text-sm";
  }

  // Reset selected faction
  gameState.selectedFaction = null;

  // Remove selected class from all faction cards
  document.querySelectorAll(".faction-card").forEach((card) => {
    card.classList.remove("selected");
    card.classList.add("border-gray-600");
  });

  // Clear skills display
  const skillsSection = document.getElementById("skills-section");
  if (skillsSection) {
    skillsSection.innerHTML = "";
  }

  // Reset header background
  updateHeaderBackground(null);

  // Update faction availability based on existing characters
  updateFactionAvailability();

  // Update create button state
  updateCreateButton();
}

export function switchToCharacter(characterId) {
  const character = gameState.characters.find((c) => c.id === characterId);
  debug(CH.ui, "Switching to character:", characterId, character);

  if (!character) {
    console.error("Character not found:", characterId);
    return false;
  }

  debug(CH.ui, "checking raid status:", gameState.currentRaid.raidOwnerId, gameState.currentRaid.raidInProgress, gameState.currentRaid.raidCompleted);
  // Check if there's an active raid that belongs to a different character
  if (
    gameState.currentRaid.raidOwnerId &&
    gameState.currentRaid.raidOwnerId !== characterId &&
    (gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted)
  ) {
    const raidOwner = gameState.characters.find((c) => c.id === gameState.currentRaid.raidOwnerId);
    const ownerName = raidOwner ? raidOwner.name : "another character";
    addNotification(`Cannot switch characters during ${ownerName}'s active raid`, "error");
    return false;
  }

  debug(CH.ui, "saving current character state if needed");
  // Save current character state if switching from another character
  if (gameState.character && gameState.character.id !== characterId) {
    const currentCharIndex = gameState.characters.findIndex((c) => c.id === gameState.character.id);
    if (currentCharIndex !== -1) {
      gameState.characters[currentCharIndex] = { ...gameState.character };
    }
  }

  if (gameState.character && gameState.character.faction) document.body.classList.remove(gameState.character.faction);

  debug(CH.ui, "switching to new character state");
  // Switch to new character - ensure inventory exists
  gameState.character = {
    ...character,
    inventory: character.inventory || {},
    artifacts: character.artifacts || [],
    equippedArtifacts: character.equippedArtifacts || { skillSlot: null, utilitySlot: null },
    equippedTechniqueGear: character.equippedTechniqueGear || {},
    techniqueTabSelections: character.techniqueTabSelections || {},
    isAlive: character.isAlive == null ? true : character.isAlive,
    injuries: character.injuries || [],
  };
  gameState.activeCharacterId = characterId;

  debug(CH.ui, "Updating UI for new character");

  if (gameState.character && gameState.character.faction) document.body.classList.add(gameState.character.faction);

  // Update UI first
  updateUI();
  displayCharacter();

  // Update tab visibility based on raid ownership
  updateTabVisibility();

  // Refresh Alpine inventory store to reflect character-specific inventory after UI update
  setTimeout(() => {
    refreshAlpineInventory();
  }, 100);

  // Update inventory header with character name
  updateInventoryCharacterHeader();

  // Save the game state to persist the character switch
  saveGameState();

  //addNotification(`Switched to ${character.name}`, 'success');
  return true;
}

export function getAvailableCharacters() {
  return gameState.characters.filter((c) => !c.isMarketplace);
}

export function getMarketplaceCharacters() {
  return gameState.characters.filter((c) => c.isMarketplace);
}

export function contractOutCharacter(characterId, durationHours = 24, rewardPercentage = 15) {
  // If this is the active character, use gameState.character, otherwise lookup by characterId
  const character = (characterId === gameState.activeCharacterId && gameState.character) ? gameState.character : gameState.characters.find((c) => c.id === characterId);
  if (!character) {
    console.error("Character not found:", characterId);
    return false;
  }

  // Register with multiplayer marketplace if character has multiplayerId
  debug(CH.ui, "Listing character on marketplace:", character.name, character.multiplayerId, typeof multiplayer);
  if (character.multiplayerId && typeof multiplayer !== "undefined") {
    multiplayer
      .listCharacterOnMarketplace(character.multiplayerId, durationHours, rewardPercentage)
      .then(() => {
        // Mark character as on marketplace
        character.isMarketplace = true;
        character.rewardPercentage = rewardPercentage;
        character.marketplaceData = {
          listedAt: Date.now(),
          expiresAt: Date.now() + durationHours * 60 * 60 * 1000,
          durationHours: durationHours,
          rewardPercentage: rewardPercentage,
        };
        saveGameState();
        addNotification(`${character.name} is now available for hire on the marketplace`, "success");
        displayCharacter(); // Refresh character display
      })
      .catch((error) => {
        console.error("Failed to list character on marketplace:", error);
        // Revert local changes if multiplayer fails
        character.isMarketplace = false;
        character.marketplaceData = null;
        saveGameState();
        addNotification("Failed to list character on marketplace", "error");
      });
  } else {
    addNotification(`${character.name} is cannot be listed for hire on the marketplace`, "error");
  }

  return true;
}

export function showContractOutModal() {
  const modal = document.createElement("div");
  modal.className = "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50";
  modal.innerHTML = `
        <div class="bg-gray-800 border border-gray-600 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-xl font-bold text-game-accent mb-4">💼 Contract Out Character</h3>
            <p class="text-gray-300 mb-4">
                Contract out <span class="text-white font-bold">${gameState.character.name}</span> to the marketplace?
                They will be available for other players to hire as contractors.
            </p>
            <div class="mb-4">
                <label class="block text-sm font-bold text-gray-300 mb-2">Duration:</label>
                <select id="contract-duration" class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white">
                    <option value="4">4 hours</option>
                    <option value="8">8 hours</option>
                    <option value="12">12 hours</option>
                    <option value="24" selected>24 hours</option>
                </select>
                <p class="text-xs text-gray-400 mt-1">Contracts are for long absences — even un-hired, a completed contract pays out.</p>
            </div>
            <div class="mb-4">
                <label class="block text-sm font-bold text-gray-300 mb-2">Reward Percentage:</label>
                <div class="flex items-center space-x-2">
                    <input id="contract-percentage" type="number" min="10" max="20" value="15" class="w-20 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-center">
                    <span class="text-gray-300">% of raid rewards</span>
                </div>
                <p class="text-xs text-gray-400 mt-1">Set the percentage of raid earnings you'll receive (10-20%)</p>
            </div>
            <div class="flex space-x-3">
                <button id="confirm-contract-out" class="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded">
                    Contract Out
                </button>
                <button id="cancel-contract-out" class="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
                    Cancel
                </button>
            </div>
        </div>
    `;

  document.body.appendChild(modal);

  document.getElementById("confirm-contract-out").addEventListener("click", () => {
    const duration = parseInt(document.getElementById("contract-duration").value);
    const percentage = parseInt(document.getElementById("contract-percentage").value);

    // Validate percentage range
    if (percentage < 10 || percentage > 20) {
      addNotification("Reward percentage must be between 10% and 20%", "error");
      return;
    }

    if (contractOutCharacter(gameState.activeCharacterId, duration, percentage)) {
      displayCharacterSelector();
    }
    document.body.removeChild(modal);
  });

  document.getElementById("cancel-contract-out").addEventListener("click", () => {
    document.body.removeChild(modal);
  });
}

export async function removeFromMarketplace(characterId) {
  // Writes must go to the active character's live copy (see liveCharacter).
  const character = liveCharacter(gameState.characters.find((c) => c.id === characterId));
  if (!character) {
    console.error("Character not found:", characterId);
    return false;
  }

  // Remove from multiplayer marketplace if applicable
  if (character.multiplayerId && typeof multiplayer !== "undefined") {
    try {
      await multiplayer.removeCharacterFromMarketplace(character.multiplayerId);

      // Only update local state after successful server response
      character.isMarketplace = false;
      character.marketplaceData = null;
      addNotification(`${character.name} removed from marketplace`, "success");
      displayCharacter(); // Refresh character display
      return true;
    } catch (error) {
      console.error("Failed to remove character from marketplace:", error);
      addNotification("Failed to withdraw character from marketplace", "error");
      return false;
    }
  } else {
    // For offline mode, update local state directly
    character.isMarketplace = false;
    character.marketplaceData = null;
    addNotification(`${character.name} removed from marketplace`, "success");
    displayCharacter(); // Refresh character display
    return true;
  }
}

// Generic confirm dialog matching the game's modal styling. Replaces native
// confirm(), which can't be styled and blocks the main thread.
export function showConfirmModal({ title, bodyHtml, confirmText = "Confirm", cancelText = "Cancel", confirmClass = "bg-yellow-600 hover:bg-yellow-700", onConfirm }) {
  const modal = document.createElement("div");
  modal.className = "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50";
  modal.innerHTML = `
        <div class="bg-gray-800 border border-gray-600 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-xl font-bold text-game-accent mb-4">${title}</h3>
            <div class="text-gray-300 mb-4">${bodyHtml}</div>
            <div class="flex space-x-3">
                <button data-role="confirm" class="flex-1 ${confirmClass} text-white font-bold py-2 px-4 rounded">${confirmText}</button>
                <button data-role="cancel" class="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">${cancelText}</button>
            </div>
        </div>
    `;

  document.body.appendChild(modal);
  const close = () => { if (modal.parentNode) modal.parentNode.removeChild(modal); };
  modal.querySelector('[data-role="confirm"]').addEventListener("click", () => {
    close();
    if (onConfirm) onConfirm();
  });
  modal.querySelector('[data-role="cancel"]').addEventListener("click", close);
}

export async function withdrawFromMarketplace(characterId) {
  // Active-character-aware: its live marketplaceData lives on gameState.character.
  const character = liveCharacter(gameState.characters.find((c) => c.id === characterId));
  if (!character) {
    console.error("Character not found:", characterId);
    return false;
  }

  if (!character.isMarketplace) {
    addNotification("Character is not on the marketplace", "error");
    return false;
  }

  // Local-only (offline) listing: nothing to settle server-side, just remove.
  if (!character.multiplayerId) {
    const ok = await removeFromMarketplace(characterId);
    if (ok) {
      displayCharacter();
      displayCharacterSelector();
      saveGameState();
    }
    return ok;
  }

  // Server-listed contract: withdrawing settles it for the time actually listed,
  // so confirm first and show what that settlement is worth. The estimate uses
  // the same shared model the server settles with (raidEconomics-core), so the
  // preview and the payout agree. It assumes the character wasn't hired — if it
  // was, the real hire payment already covered it and the server pays nothing.
  const listedAt = character.marketplaceData?.listedAt;
  const elapsedHours = listedAt ? (Date.now() - listedAt) / 3_600_000 : 0;
  const estimate = contractCompletionReward({
    level: character.level || 1,
    durationHours: elapsedHours,
    rewardPercentage: character.marketplaceData?.rewardPercentage || 15,
  });

  const listedFor = `Listed for <span class="text-white font-bold">${elapsedHours.toFixed(1)}h</span>`;
  const bodyHtml = estimate.qualifies
    ? `<p class="mb-2">${listedFor}. Withdrawing <span class="text-white font-bold">${character.name}</span> now settles the contract early.</p>
       <div class="bg-gray-700/50 border border-gray-600 rounded p-3 mb-2">
         <div class="text-sm">Estimated settlement:</div>
         <div class="text-game-accent font-bold">💰 ~${estimate.credits} credits &nbsp; ⭐ ~${estimate.xp} XP</div>
         <div class="text-xs text-gray-400 mt-1 hidden">Based on ${estimate.simulatedRaids} simulated raid${estimate.simulatedRaids === 1 ? "" : "s"}.</div>
       </div>
       <p class="text-xs text-gray-400">Leaving the contract to run its full term would pay more.</p>`
    : `<p class="mb-2">${listedFor} — under the <span class="text-white font-bold">~${minContractHoursToEarn(character.level || 1).toFixed(1)}h</span> needed to earn a contract payout.</p>
       <p class="text-yellow-400">If ${character.name} wasn't hired, withdrawing now earns <span class="font-bold">nothing</span>.</p>
       <p class="text-xs text-gray-400 mt-2">Leaving the contract running pays out even if nobody hires them.</p>`;

  showConfirmModal({
    title: estimate.qualifies ? "💼 Withdraw Contract Early?" : "⚠️ Withdraw Without Payout?",
    bodyHtml,
    confirmText: estimate.qualifies ? "Withdraw" : "Withdraw Anyway",
    confirmClass: estimate.qualifies ? "bg-yellow-600 hover:bg-yellow-700" : "bg-red-600 hover:bg-red-700",
    onConfirm: () => {
      performContractWithdrawal(character, characterId).catch((err) =>
        console.error("Failed to withdraw from marketplace:", err)
      );
    },
  });

  return true;
}

// Settle + remove a server-listed contract. Split out of withdrawFromMarketplace
// so the confirm modal drives it.
async function performContractWithdrawal(character, characterId) {
  // Settle the completion payout for time listed BEFORE removing the row (the
  // server claim needs the active listing to still exist). The server is
  // authoritative on the amount and pays nothing if the character was actually
  // hired or the elapsed time is too short. Best-effort — a settle error must
  // not block the withdrawal itself.
  let settled = null;
  try {
    settled = await multiplayer.claimContractCompletion(character.multiplayerId);
  } catch (err) {
    console.error("Failed to settle contract on withdraw:", err);
  }

  const success = await removeFromMarketplace(characterId);
  if (success) {
    displayCharacter();
    displayCharacterSelector();
    saveGameState();
    // Collect the settlement payment through the shared pending-payment flow
    // (Contract Beacon bonus + "received credits" notification for free).
    if (settled?.claimed) {
      checkAndApplyPendingPayments().catch((err) => console.error("Error checking payments:", err));
    }
  }

  return success;
}

// Helper export function to get available roles/quotes from character skills
export function getAvailableRolesAndQuotes() {
  const characterSkills = Object.keys(gameState.character.skills || {}).filter((skill) => gameState.character.skills[skill] > 0);

  const availableOptions = [];

  characterSkills.forEach((skill) => {
    const flavorData = config.contractorSkillFlavors[skill];
    if (flavorData) {
      availableOptions.push({
        skill: skill,
        roles: flavorData.roles,
        quotes: flavorData.quotes,
      });
    }
  });

  return availableOptions;
}

// Character rename functionality
export function showRenameModal() {
  // Editing is locked while a raid is actively running (the button is also
  // disabled, but guard the action too - render timing isn't guaranteed).
  if (gameState.currentRaid?.raidInProgress) {
    addNotification("Can't edit your character while a raid is in progress", "info");
    return;
  }
  const modal = document.getElementById("rename-character-modal");
  const nameInput = document.getElementById("character-name-input");
  const roleSelect = document.getElementById("character-role-select");
  const quoteSelect = document.getElementById("character-quote-select");

  if (modal && nameInput && roleSelect && quoteSelect) {
    // Set current name in input
    nameInput.value = gameState.character.name || "";

    // Populate role and quote dropdowns
    const availableOptions = getAvailableRolesAndQuotes();

    // Build role options grouped by skill
    roleSelect.innerHTML = '<option value="">Select a role...</option>';
    const allRoles = new Map(); // Map role to skill for quote lookup

    availableOptions.forEach(({ skill, roles }) => {
      const skillData = config.factionSkills[gameState.character.faction].skills[skill];
      const skillName = skillData ? skillData.name : skill;

      roles.forEach((role) => {
        const option = document.createElement("option");
        option.value = role;
        option.textContent = `${role} (${skillName})`;
        option.dataset.skill = skill;
        roleSelect.appendChild(option);
        allRoles.set(role, skill);
      });
    });

    // Set current role if exists
    if (gameState.character.title) {
      roleSelect.value = gameState.character.title;
    }

    // Update quote dropdown when role changes
    const updateQuoteDropdown = () => {
      const selectedRole = roleSelect.value;
      quoteSelect.innerHTML = '<option value="">Select a quote...</option>';

      if (selectedRole) {
        const selectedSkill = allRoles.get(selectedRole);
        const skillData = availableOptions.find((opt) => opt.skill === selectedSkill);

        if (skillData) {
          skillData.quotes.forEach((quote) => {
            const option = document.createElement("option");
            option.value = quote;
            option.textContent = quote;
            quoteSelect.appendChild(option);
          });
        }
      }

      // Set current quote if exists
      if (gameState.character.quote) {
        quoteSelect.value = gameState.character.quote;
      }
    };

    // Initial quote population
    updateQuoteDropdown();

    // Update quotes when role changes
    roleSelect.addEventListener("change", updateQuoteDropdown);

    nameInput.focus();
    nameInput.select();

    modal.classList.remove("hidden");

    // Setup event listeners
    setupRenameModalListeners();
  }
}

export function hideRenameModal() {
  const modal = document.getElementById("rename-character-modal");
  if (modal) {
    modal.classList.add("hidden");
  }
}

export function setupRenameModalListeners() {
  const modal = document.getElementById("rename-character-modal");
  const nameInput = document.getElementById("character-name-input");
  const confirmBtn = document.getElementById("confirm-rename-btn");
  const cancelBtn = document.getElementById("cancel-rename-btn");
  const closeBtn = document.getElementById("close-rename-modal");
  const validationMessage = document.getElementById("name-validation-message-modal");

  // Remove existing listeners to prevent duplicates
  const newConfirmBtn = confirmBtn.cloneNode(true);
  const newCancelBtn = cancelBtn.cloneNode(true);
  const newCloseBtn = closeBtn.cloneNode(true);

  confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
  cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);
  closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);

  // Real-time validation
  const debouncedValidate = debounce(async () => {
    const name = nameInput.value.trim();

    if (name && name !== gameState.character.name) {
      validationMessage.className = "text-sm mt-1 text-gray-400";
      validationMessage.textContent = "Checking name availability...";
    }

    const result = await validateCharacterName(name, gameState.character.name);
    validationMessage.className = `text-sm mt-1 ${result.className}`;
    validationMessage.textContent = result.message;
  }, 500);

  nameInput.addEventListener("input", debouncedValidate);

  // Add event listeners
  newConfirmBtn.addEventListener("click", handleRename);
  newCancelBtn.addEventListener("click", hideRenameModal);
  newCloseBtn.addEventListener("click", hideRenameModal);

  // Handle Enter key
  nameInput.addEventListener("keypress", (e) => {
    if (e.key === "Enter") {
      handleRename();
    }
  });

  // Handle Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      const modal = document.getElementById("rename-character-modal");
      if (modal && !modal.classList.contains("hidden")) {
        hideRenameModal();
        document.removeEventListener("keydown", handleEscapeKey);
      }
    }
  });

  // Close on background click (only if mousedown started on background)
  let mousedownTarget = null;
  modal.addEventListener("mousedown", (e) => {
    mousedownTarget = e.target;
  });
  modal.addEventListener("click", (e) => {
    if (e.target === modal && mousedownTarget === modal) {
      hideRenameModal();
    }
  });
}

export async function handleRename() {
  const nameInput = document.getElementById("character-name-input");
  const roleSelect = document.getElementById("character-role-select");
  const quoteSelect = document.getElementById("character-quote-select");

  const newName = nameInput.value.trim();
  const newRole = roleSelect.value || null;
  const newQuote = quoteSelect.value || null;

  // Check if anything changed
  const nameChanged = newName !== gameState.character.name;
  const roleChanged = newRole !== gameState.character.title;
  const quoteChanged = newQuote !== gameState.character.quote;

  if (!nameChanged && !roleChanged && !quoteChanged) {
    hideRenameModal();
    return;
  }

  // Validate name if it changed
  if (nameChanged) {
    const validation = await validateCharacterName(newName, gameState.character.name);
    if (!validation.valid) {
      addNotification(`❌ ${validation.message}`, "error");
      nameInput.focus();
      return;
    }
  }

  const oldName = gameState.character.name;
  const oldRole = gameState.character.title;
  const oldQuote = gameState.character.quote;

  // Update character data
  gameState.character.name = newName;
  gameState.character.title = newRole;
  gameState.character.quote = newQuote;

  try {
    // Update in multiplayer system if character is registered
    if (gameState.character.multiplayerId) {
      await multiplayer.updateCharacter(gameState.character.multiplayerId, {
        name: newName,
        title: newRole,
        quote: newQuote,
      });
    }

    // Save the game state
    saveGameState();

    // Update the display
    displayCharacter();

    // Close modal
    hideRenameModal();

    // Show success notification
    const changes = [];
    if (nameChanged) changes.push("name");
    if (roleChanged) changes.push("role");
    if (quoteChanged) changes.push("quote");
    addNotification(`✅ Character ${changes.join(", ")} updated`, "success");
  } catch (error) {
    // Rollback changes on error
    gameState.character.name = oldName;
    gameState.character.title = oldRole;
    gameState.character.quote = oldQuote;
    console.error("Failed to update character in multiplayer system:", error);
    addNotification("❌ Failed to update character in multiplayer system", "error");
    nameInput.focus();
  }
}

export function addCharacterRenameButtonListener() {
  // Setup rename button listener
  document.addEventListener("click", (e) => {
    if (e.target && e.target.id === "rename-character-btn") {
      showRenameModal();
    }
  });
}

//
// Character creation UI functions
//

export function displayCharacterSelector() {
  const selectorContainer = document.getElementById("character-selector");
  if (!selectorContainer) return;

  selectorContainer.innerHTML = "";

  const maxCharacters = 3;
  let firstEmptySlot = true;

  // Display existing characters
  gameState.characters.forEach((char) => {
    const isActive = gameState.activeCharacterId === char.id;
    const character = isActive ? gameState.character : char;
    const isMarketplace = character.isMarketplace;
    const hasRaid = gameState.currentRaid.raidOwnerId == char.id;

    const cardDiv = document.createElement("div");
    cardDiv.className = `character-selector-card min-w-64 max-w-80 min-h-16 p-2 py-1 rounded-tr-lg rounded-tl-lg transition-all border-2 border-b-0 ${
      isActive ? "border-transparent bg-white/[0.09]" : "border-transparent bg-gray-800/30"
    }`;

    const faction = config.factionSkills[character.faction];
    const raidStatus = hasRaid ? `<div class="text-amber-400 text-sm font-bold">In Raid</div>` : "";

    let marketplaceStatus = "";
    if (isMarketplace && character.marketplaceData) {
      const timeLeftMs = Math.max(0, character.marketplaceData.expiresAt - Date.now());
      const hours = Math.floor(timeLeftMs / (60 * 60 * 1000));
      const minutes = String(Math.ceil(timeLeftMs / (60 * 1000)) - (hours*60)).padStart(2, '0'); ;
      marketplaceStatus = `<div class="text-sm hidden [@media(min-height:769px)]:inline-block"><span class="text-yellow-600 font-bold">💼 Marketplace</span> <span id="${char.id}_marketplace_time" class="text-gray-400 mr-2">${hours}:${minutes}</span></div>`;
    }

    const xpToNext = calculateXPToNextLevel(character.level || 1);
    const xpPercentage = ((character.xp || 0) / xpToNext) * 100;

    cardDiv.innerHTML = `
            <div class="flex items-start justify-between h-full">
                <div class="flex gap-2 flex-1 h-full">
                    <!-- Avatar with proper background container -->
                    <div class="${
                      !isActive ? "opacity-40" : ""
                    } relative bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-500 rounded-lg w-24 overflow-hidden group flex-shrink-0 h-24 hidden [@media(min-height:769px)]:block">
                        <div class="absolute inset-0 flex items-center justify-center">
                            <div class="contractor-avatar-placeholder transform group-hover:scale-110 transition-transform duration-200" ${avatarDataAttrs(character)}></div>
                        </div>
                        <div class="absolute left-1 top-1 bg-black bg-opacity-30 border border-gray-500 rounded-full w-6 h-6 flex items-center justify-center text-sm cursor-pointer" title="${
                          faction.name
                        }">${faction.icon || "🏢"}</div>
                        <!-- HP Status overlay -->
                        <div class="absolute bottom-0 left-0 bg-black bg-opacity-70 text-white p-[2px] rounded-tl text-xs m-[1px]">
                            ❤️${character.currentHp}
                        </div>
                    </div>
                    <div class="flex-grow relative pr-2 h-full">
                        <div class="flex items-center gap-2 pb-2 font-bold ${isActive ? "text-white" : "text-white/50"} nowrap">
                          <div class="bg-black bg-opacity-30 border border-gray-500 rounded-full w-6 h-6 flex items-center justify-center text-sm cursor-pointer block [@media(min-height:769px)]:hidden" title="${faction.name}">${faction.icon || "🏢"}</div>
                          ${character.name}
                          <div class="${isActive ? "text-blue-300" : "text-blue-300/50"} text-sm font-semibold nowrap block [@media(min-height:769px)]:hidden">Lv.${character.level || 1}</div>
                        </div>
                        <div class="${isActive ? "text-blue-300" : "text-blue-300/50"} text-sm font-semibold nowrap hidden [@media(min-height:769px)]:block">Level ${character.level || 1}</div>
                        <div class="hidden bg-gray-700 rounded-lg h-4 overflow-hidden relative w-full">
                            <div class="bg-gradient-to-r from-blue-500 to-blue-400 h-full transition-all duration-300" style="width: ${xpPercentage}%;"></div>
                            <div class="absolute inset-0 flex items-center justify-center text-white text-xs font-semibold">
                                ${character.xp || 0}/${xpToNext}
                            </div>
                        </div>
                        ${marketplaceStatus}
                        ${raidStatus}
                        <div class="absolute bottom-0 right-0 z-10">
                        ${isActive
                            ? `
                            <div class="bg-amber-500 text-black hover:text-gray text-xs px-3 py-1 rounded font-bold">
                                Active
                            </div>
                        `
                            : `
                            <button class="switch-character-btn text-black hover:text-gray text-xs px-3 py-1 rounded font-bold" data-character-id="${character.id}">
                                Switch
                            </button>
                        `
                        }
                        </div>
                    </div>
                </div>
            </div>
        `;

    // Add switch button event listener
    if (!isActive) {
      setTimeout(() => {
        const switchBtn = cardDiv.querySelector(".switch-character-btn");
        if (switchBtn) {
          switchBtn.addEventListener("click", () => {
            switchToCharacter(character.id);
            displayCharacterSelector();
          });
        }
      }, 0);
    }

    selectorContainer.appendChild(cardDiv);
  });

  // Add placeholder cards for empty slots
  const emptySlots = maxCharacters - gameState.characters.length;
  for (let i = 0; i < emptySlots; i++) {
    const cardDiv = document.createElement("div");
    cardDiv.className = "character-selector-card min-w-64 max-w-80 p-2 border-2 border-b-0 border-dashed border-gray-600 bg-gray-800/30 rounded-t-lg";

    cardDiv.innerHTML = `
            <div class="flex items-end gap-3 h-full">
                <!-- Empty avatar placeholder -->
                <div class="relative bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-500 border-dashed rounded-lg h-24 w-24 overflow-hidden group flex-shrink-0 hidden [@media(min-height:769px)]:block">
                    <div class="absolute inset-0 flex items-center justify-center">
                        <div class="contractor-avatar-placeholder transform group-hover:scale-110 transition-transform duration-200"></div>
                    </div>
                </div>
                ${
                  firstEmptySlot
                    ? `
                    <div class="flex-grow flex flex-col items-end justify-end">
                        <button id="create-character-slot-btn" class="bg-blue-400 hover:bg-blue-300 text-black hover:text-gray text-xs px-3 py-1 rounded font-bold">
                            Create Character
                        </button>
                    </div>
                `
                    : ""
                }
            </div>
        `;

    selectorContainer.appendChild(cardDiv);

    if (firstEmptySlot) {
      firstEmptySlot = false;
      // Add event listener to the button
      setTimeout(() => {
        const createBtn = document.getElementById("create-character-slot-btn");
        if (createBtn) {
          createBtn.addEventListener("click", () => {
            document.getElementById("character-display").classList.add("hidden");
            document.getElementById("character-creator").classList.remove("hidden");

            const cancelBtn = document.getElementById("cancel-character-creation");
            if (cancelBtn) {
              cancelBtn.classList.remove("hidden");
            }
          });
        }
      }, 0);
    }
  }
}

export function addCharacterCreationButtonEvents() {
  // Create new character button
  const createNewCharBtn = document.getElementById("create-new-character-btn");
  debug(CH.ui, "Setting up create new character button:", createNewCharBtn);
  if (createNewCharBtn) {
    createNewCharBtn.addEventListener("click", () => {
      // Reset character creation form (includes updateFactionAvailability)
      resetCharacterCreationForm();

      // Show character creator
      document.getElementById("character-display").classList.add("hidden");
      document.getElementById("character-creator").classList.remove("hidden");
      // Show cancel button if there's an active character
      updateCancelCreatorButtonVisibility();
    });
  }

  // Cancel character creation button
  const cancelBtn = document.getElementById("cancel-character-creation");
  if (cancelBtn) {
    cancelBtn.addEventListener("click", () => {
      // Return to character display
      document.getElementById("character-creator").classList.add("hidden");
      updateHeaderBackground(gameState.character.faction);
      document.getElementById("character-display").classList.remove("hidden");
    });
  }
}

// Update cancel button visibility based on whether there's an active character
export function updateCancelCreatorButtonVisibility() {
  const cancelBtn = document.getElementById("cancel-character-creation");
  debug(CH.ui, "cancel button", cancelBtn);
  if (cancelBtn) {
    if (gameState.character && gameState.characters.length > 0) {
      cancelBtn.classList.remove("hidden");
    } else {
      cancelBtn.classList.add("hidden");
    }
  }
}
